import { FC, useEffect } from "react";
import { Skeleton } from "@mui/material";
import { embedDashboard } from "@superset-ui/embedded-sdk";
import { AxiosResponse } from "axios";
import { postAuthenticationAPI } from "../../../../../../datasources/api";

interface Props {
	dashboardId: string;
}

interface SupersetGuestTokenResponse {
	token: string;
}

const SupersetDashboard: FC<Props> = ({ dashboardId }) => {
	const getToken = async () => {
		const response = await postAuthenticationAPI.get<null, AxiosResponse<SupersetGuestTokenResponse>>(
			`/auth/sign-in/dashboard/superset/${dashboardId}`
		);
		return response.data.token;
	};

	useEffect(() => {
		// Ensure the iframe created by embedDashboard fills the container.
		// The CSS uses !important to override any sizing the embed code might add.
		const styleId = "results-dashboard-iframe-style";
		if (!document.getElementById(styleId)) {
			const style = document.createElement("style");
			style.id = styleId;
			style.innerHTML = `
                #dashboard > iframe {
                    width: 100% !important;
                    height: 100% !important;
                    border: 0 !important;
                    display: block;
                }
            `;
			document.head.appendChild(style);
		}

		const mountPoint = document.getElementById("dashboard");
		const supersetDomain = import.meta.env.VITE_SUPERSET_URL;

		const embed = async () => {
			const mp = document.getElementById("dashboard");
			mp &&
				supersetDomain &&
				(await embedDashboard({
					id: dashboardId,
					supersetDomain,
					mountPoint: mp,
					fetchGuestToken: () => getToken(),
					dashboardUiConfig: {
						hideTitle: true,
						filters: {
							visible: true,
							expanded: false,
						},
					},
					iframeSandboxExtras: [
						"allow-same-origin",
						"allow-top-navigation",
						"allow-popups-to-escape-sandbox",
						"allow-downloads",
						"allow-scripts",
						"allow-presentation",
						"allow-forms",
						"allow-popups",
					],
				}));
		};

		if (mountPoint) {
			embed();
		}
	}, [dashboardId]);

	if (dashboardId === "") {
		return <></>;
	}

	return (
		<div
			id="dashboard"
			style={{
				width: "100%",
				flex: 1,
				minHeight: 0,
				overflow: "hidden",
			}}
		>
			<Skeleton variant="rounded" width="100%" height="100%" />
		</div>
	);
};

export default SupersetDashboard;
